// State and behaviour of the main map's "On this map" control: the segment in the Maps
// pill, the tray it opens, and the undo toast after a clear.

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { i18n } from '../../../i18n';
import {
  activeItems,
  CHIP_LIMIT,
  glyphColorFor,
  HIKE_LISTS_KEY,
  itemsByGroup,
  ON_THIS_MAP_GROUPS,
  OnThisMapGroup,
  OnThisMapItem,
  OVERLAY_CHIP_STYLES,
  RESTORED_TOAST_MS,
  summariseNames,
  UNDO_TOAST_MS,
} from '../../../utils/onThisMap';
import { clearGroups, OnThisMapSnapshot, readOnThisMapState, restoreSnapshot } from './onThisMapController';
import type { OnThisMapChip } from '../components/on_this_map/OnThisMapSegment.vue';
import type { OnThisMapRow } from '../components/on_this_map/OnThisMapTray.vue';

const t = i18n.global.t;

function overlayName(key: string): string {
  switch (key) {
    case 'heatmap': return t('base_map_dialog.base_map_data_overlays_tab.heatmap');
    case 'slope': return t('base_map_dialog.base_map_data_overlays_tab.slope_angle');
    case 'hazards': return t('trail_hazards.toggle.title');
    case 'cell': return t('base_map_dialog.base_map_data_overlays_tab.cell_coverage');
    case 'officialHikes': return t('base_map_dialog.base_map_data_overlays_tab.hiiker_hikes');
    case 'landAccess': return t('base_map_dialog.base_map_data_overlays_tab.public_access');
    case 'cycling': return t('base_map_dialog.base_map_data_overlays_tab.cycling_routes');
    case 'weather:temperature': return t('base_map_dialog.base_map_data_overlays_tab.temperature');
    case 'weather:cloud': return t('base_map_dialog.base_map_data_overlays_tab.cloud');
    case 'weather:rainfall': return t('base_map_dialog.base_map_data_overlays_tab.rainfall');
    case 'weather:snowfall': return t('base_map_dialog.base_map_data_overlays_tab.snowfall');
    case 'weather:wind': return t('base_map_dialog.base_map_data_overlays_tab.wind');
    default: return key;
  }
}

function groupTitle(group: OnThisMapGroup): string {
  switch (group) {
    case 'pois': return t('map.on_this_map_control.group_pois');
    case 'overlays': return t('map.on_this_map_control.group_overlays');
    case 'hazards': return t('trail_hazards.toggle.title');
    default: return t('map.on_this_map_control.group_hikes');
  }
}

/**
 * A tray row's second line: the names of what is on, as on iOS and Android — for the
 * Hazards row, its one item, "Hazards". POIs also lead with how many types.
 */
function groupSummary(group: OnThisMapGroup, names: string[]): string {
  if (group !== 'pois') return summariseNames(names);
  return t('map.on_this_map_control.poi_summary', {
    types: t('map.on_this_map_control.n_types', names.length),
    names: summariseNames(names),
  });
}

// The Hazards row alone reads "1 layer cleared", like Your hikes: it isn't one of the
// Overlays tab's overlays any more.
function clearedMessage(groups: OnThisMapGroup[], count: number): string {
  if (groups.length === 1 && groups[0] === 'pois') return t('map.on_this_map_control.n_poi_types_cleared', count);
  if (groups.length === 1 && groups[0] === 'overlays') return t('map.on_this_map_control.n_overlays_cleared', count);
  return t('map.on_this_map_control.n_layers_cleared', count);
}

export function useOnThisMap(options: { root: () => HTMLElement | null | undefined }) {
  const store = useStore();

  const items = computed(() => activeItems(readOnThisMapState(store)));
  const byGroup = computed(() => itemsByGroup(items.value));
  const count = computed(() => items.value.length);

  const poiFor = (type: string) =>
    (store.state.mapDataPOIs?.poiList ?? []).find((poi: any) => poi.mapboxFilterType() === type);

  const itemName = (item: OnThisMapItem): string => {
    if (item.group === 'pois') return poiFor(item.key)?.prettyName() ?? item.key;
    if (item.group === 'hikes' && item.key === HIKE_LISTS_KEY) {
      return t('base_map_dialog.base_map_data_overlays_tab.hike_lists');
    }
    if (item.group === 'hikes') return store.state.mapDataOverlays?.trailOptions?.[item.key] ?? item.key;
    return overlayName(item.key);
  };

  const chips = computed<OnThisMapChip[]>(() => items.value.slice(0, CHIP_LIMIT).map((item) => {
    const id = `${item.group}:${item.key}`;
    if (item.group === 'pois') {
      const poi = poiFor(item.key);
      let image: string | undefined;
      try { image = poi?.getPublicImageIcon(); } catch { image = undefined; }
      return { id, shape: 'circle', fill: poi?.getColor() ?? '#ACB0BF', image };
    }
    const style = OVERLAY_CHIP_STYLES[item.key] ?? { fill: '#505259', glyph: { symbol: 'layers' } };
    return {
      id,
      shape: 'square',
      fill: style.fill,
      glyphColor: glyphColorFor(style.fill),
      ...('symbol' in style.glyph ? { symbol: style.glyph.symbol } : { svgPath: style.glyph.svgPath }),
    };
  }));

  const rows = computed<OnThisMapRow[]>(() => ON_THIS_MAP_GROUPS
    .filter((group) => byGroup.value[group].length > 0)
    .map((group) => ({
      group,
      title: groupTitle(group),
      summary: groupSummary(group, byGroup.value[group].map(itemName)),
    })));

  const segmentLabel = computed(() => {
    const layers = t('map.on_this_map_control.n_layers', count.value);
    const pois = byGroup.value.pois.length;
    // Everything that isn't a POI type counts as an overlay here, hazards included.
    const overlays = byGroup.value.overlays.length + byGroup.value.hazards.length + byGroup.value.hikes.length;
    if (pois === 0 || overlays === 0) return t('map.on_this_map_control.segment_label_single', { layers });
    return t('map.on_this_map_control.segment_label', {
      layers,
      pois: t('map.on_this_map_control.n_poi_types', pois),
      overlays: t('map.on_this_map_control.n_overlays', overlays),
    });
  });

  // ─── Tray ────────────────────────────────────────────────────────────────
  const trayOpen = ref(false);

  // Focus goes back to the segment. If a clear removed it, keyboard users land on the plain
  // Maps pill instead; pointer users don't, as the theme dims a focused pill.
  const focusSegmentOrMaps = ({ mapsFallback = true }: { mapsFallback?: boolean } = {}) => {
    const root = options.root();
    const segment = root?.querySelector<HTMLElement>('.otm-segment');
    if (segment) {
      segment.focus();
    } else if (mapsFallback) {
      root?.querySelector<HTMLElement>('.trail-map-buttons button')?.focus();
    }
  };

  const closeTray = ({ refocus }: { refocus: boolean }) => {
    if (!trayOpen.value) return;
    trayOpen.value = false;
    if (refocus) nextTick(() => focusSegmentOrMaps());
  };

  const toggleTray = () => {
    if (trayOpen.value) closeTray({ refocus: true });
    else if (count.value > 0) trayOpen.value = true;
  };

  // Something else switched the last layer off while the tray was open.
  watch(count, (value) => { if (value === 0) closeTray({ refocus: false }); });

  // ─── Announcements ───────────────────────────────────────────────────────
  const announcement = ref('');
  const announce = (text: string) => {
    // Clear first so repeating the same sentence is still announced.
    announcement.value = '';
    nextTick(() => { announcement.value = text; });
  };

  // ─── Toast + undo ────────────────────────────────────────────────────────
  const toastMessage = ref('');
  const toastHasUndo = ref(false);
  let snapshot: OnThisMapSnapshot | null = null;
  let snapshotCount = 0;
  // Times out whichever toast is showing.
  let toastTimer: ReturnType<typeof setTimeout> | null = null;

  const hideToast = () => {
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = null;
    toastMessage.value = '';
    toastHasUndo.value = false;
    // The snapshot lives exactly as long as its toast.
    snapshot = null;
  };

  const showUndoToast = (message: string) => {
    hideToast();
    toastMessage.value = message;
    toastHasUndo.value = true;
    toastTimer = setTimeout(hideToast, UNDO_TOAST_MS);
  };

  let busy = false;

  const clear = async (groups: OnThisMapGroup[], { keyboard = false }: { keyboard?: boolean } = {}) => {
    if (busy) return;
    const cleared = groups.reduce((total, group) => total + byGroup.value[group].length, 0);
    if (cleared === 0) return;
    busy = true;
    closeTray({ refocus: false });
    try {
      const taken = await clearGroups(store, groups);
      const message = clearedMessage(groups, cleared);
      showUndoToast(message);
      snapshot = taken;
      snapshotCount = cleared;
      announce(t('map.on_this_map_control.cleared_announcement', { cleared: message }));
    } finally {
      busy = false;
      nextTick(() => focusSegmentOrMaps({ mapsFallback: keyboard }));
    }
  };

  const clearAll = (how?: { keyboard?: boolean }) => clear([...ON_THIS_MAP_GROUPS], how);
  const clearGroup = (group: OnThisMapGroup, how?: { keyboard?: boolean }) => clear([group], how);

  const undo = async () => {
    if (!snapshot || busy) return;
    const taken = snapshot;
    const restored = snapshotCount;
    busy = true;
    hideToast();
    try {
      await restoreSnapshot(store, taken);
    } finally {
      busy = false;
    }
    const message = t('map.on_this_map_control.n_layers_restored', restored);
    toastMessage.value = message;
    toastHasUndo.value = false;
    toastTimer = setTimeout(hideToast, RESTORED_TOAST_MS);
    announce(message);
    nextTick(() => focusSegmentOrMaps());
  };

  onBeforeUnmount(hideToast);

  return {
    count,
    chips,
    rows,
    segmentLabel,
    trayOpen,
    toggleTray,
    closeTray,
    clearAll,
    clearGroup,
    toastMessage,
    toastHasUndo,
    undo,
    announcement,
  };
}
